import {Component, OnInit, ViewChild} from '@angular/core';
import {DisplayedColumnInf} from '../../../interfaces/common/table/displayed-column-inf';
import {UserPrivilegeService} from '../../../services/common/user-privilege/user-privilege.service';
import {MatDialog} from '@angular/material/dialog';
import {SnackBarService} from '../../../services/common/snack-bar/snack-bar.service';
import {LogicalConjunctionService} from '../../../services/common/logical-conjunction/logical-conjunction.service';
import {AcDialogService, PromiseService} from 'ac-infra';
import {ConfirmationDialogComponent} from '../../../dialogs/confirmation-dialog/confirmation-dialog.component';
import {OperativeStatePipe} from '../../../pipes/operative-state/operative-state.pipe';
import {AdminStatePipe} from '../../../pipes/admin-state/admin-state.pipe';
import {RoutingServersSettingsApiService} from '../../../services/api/routing-servers/routing-servers-settings-api.service';
import {NodeApiService} from '../../../services/api/network/online/node/node-api.service';
import {ServerDialogComponent} from './server-dialog/server-dialog.component';
import {OperatorsSettingsApiService} from '../../../services/api/settings/administration/operators/operators-settings-api.service';
import {CredentialsSettingsApiService} from '../../../services/api/settings/administration/credentials/credentials-settings-api.service';
import * as _ from 'lodash';
import {ArmTableComponent} from '../../../components/arm-table/arm-table.component';
import {ServerCertificateDialogComponent} from './server-certificate-dialog/server-certificate-dialog.component';
import {TrueFalsePipe} from '../../../pipes/true-false/true-false.pipe';
import {RouterAddressPipe} from '../../../pipes/router-address/router-address.pipe';

@Component({
  selector: 'routing-servers-settings',
  templateUrl: './routing-servers-settings.component.html',
  styleUrls: ['./routing-servers-settings.component.scss']
})
export class RoutingServersSettingsComponent implements OnInit {

  displayedColumns: Array<DisplayedColumnInf> = [
    {name: 'status', display: 'Status', selected: true, width: 100, transformer: OperativeStatePipe},
    {name: 'adminState', display: 'Administrative State', selected: true, width: 150, transformer: AdminStatePipe},
    {name: 'name', display: 'Name', selected: true, width: 200},
    {name: 'address', display: 'Address', selected: true, width: 200, transformer: RouterAddressPipe},
    {name: 'port', display: 'port', selected: true, width: 100},
    {name: 'nodeProtocol', display: 'Node Protocol', selected: true, width: 150},
    {name: 'nodesNames', display: 'Nodes', selected: true, width: 200},
    {name: 'memory', display: 'Memory (GB)', selected: true, width: 100},
    {name: 'tunEnabled', display: 'WebSocket Tunnel', selected: true, width: 100, transformer: TrueFalsePipe},
  ];

  items;
  isMultipleSelection: false;
  selection;
  isUnauthorized: boolean;
  searchTerms = [{term: 'nodeId', operator: '='}];
  searchObj: any = {nodesList: []};
  parameters: any = {};
  filter;
  @ViewChild(ArmTableComponent) armTable;

  constructor(private userPrivilege: UserPrivilegeService,
              private routingServersSettingsApiService: RoutingServersSettingsApiService, public dialog: MatDialog,
              private snackBarService: SnackBarService, private logicalConjunctionService: LogicalConjunctionService,
              private dialogService: AcDialogService,
              private nodesApiService: NodeApiService,
              private operatorsSettingsApiService: OperatorsSettingsApiService,
              private credentialsSettingsApiService: CredentialsSettingsApiService) { }

  ngOnInit(): void {
    this.isUnauthorized = !this.userPrivilege.isAuthorized();
    this.refresh();
  }

  addEditItem(isEditRouter): void {

    const isEdit = isEditRouter;
    const titleItem = isEdit ? 'Edit' : 'Add';
    const parameters: any = {
      filter: {
        type: {
          operator: '=',
          value: ['ROUTER']
        }
      }
    };

    let routingServer = {};
    if (isEdit) {
      routingServer = _.cloneDeep(this.selection);
    }

    Promise.all([
      this.operatorsSettingsApiService.getOperators(parameters).toPromise(),
      this.credentialsSettingsApiService.getCredentials('ROUTER', {}).toPromise()]).then( data => {
      const routingServerObject = this.buildRoutingServerObject(isEdit, routingServer);
      const dialogComplementaryParameters = {isEdit, routerUsers: data[0], routerCredentials: data[1]};
      this.dialogService.open(ServerDialogComponent, {
        dialogData: [routingServerObject, dialogComplementaryParameters],
        title: titleItem + ' routing server',
        preSubmit: () => {
          if (this.sameUpgradeSequence(routingServerObject)) {
            const defer = PromiseService.defer();

            const confirmationDialogRef = this.dialog.open(ConfirmationDialogComponent, {
              data: {
                message: 'This Router will be upgraded simultaneously with others of the same group'
              }
            });

            confirmationDialogRef.componentInstance.onApply.subscribe(() => {
              confirmationDialogRef.close();
              defer.resolve();
            });

            confirmationDialogRef.componentInstance.onCancel.subscribe(() => {
              confirmationDialogRef.close();
              defer.reject();
            });

            return defer.promise;
          }
        },
        onSubmit: () => {
          routingServerObject.operatorId = routingServerObject.operator;
          routingServerObject.credentialsId = routingServerObject.credentials;
          if (!isEdit) {
            return this.routingServersSettingsApiService.addNewRoutingServer(routingServerObject).toPromise().then(() => {
              this.refresh();
            });
          } else {
            return this.routingServersSettingsApiService.updateRoutingServer(routingServerObject).toPromise().then(() => {
              this.refresh();
            });
          }
        },
      });
    });

  }

  removeItem(): void {

    const routingServer = _.cloneDeep(this.selection);
    const confirmationMessage = `Are you sure you want to delete the routing server: "${this.selection.name}" ?`;

    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      data: {
        message: confirmationMessage
      }
    });


    dialogRef.componentInstance.onApply.subscribe(() => {
      this.routingServersSettingsApiService.deleteRoutingServer(routingServer).subscribe(() => {
        dialogRef.close();
        this.refresh();
        this.snackBarService.display(`${this.logicalConjunctionService
          .generateAndDescription(this.selection, 'name')} successfully deleted`);
      });
    });

  }

  onSelection(selection: any): void {
    this.selection = selection;
  }

  refresh(): void {
    Promise.all([
      this.nodesApiService.getNodes(),
      this.routingServersSettingsApiService.getRoutingServers(this.generateFilterParameters()).toPromise()
    ]).then(data => {
      const dataObject = {
        nodesList: data[0],
        routingServers: data[1]
      };
      this.parameters.nodesList = dataObject.nodesList;
      this.buildDataForRoutingServersTable(dataObject);
    });
  }

  private generateFilterParameters(): any {
    const generatedParameters: any = {};

    if (this.parameters.search) {
      if (typeof this.parameters.search === 'string') {
        generatedParameters.filter = {
          searchString: {
            operator: ':',
            value: this.parameters.search
          }
        };
      } else if (typeof this.parameters.search === 'object') {
        generatedParameters.filter = this.parameters.search;
      }
    }

    return generatedParameters;
  }


  private buildDataForRoutingServersTable(dataObject): any {
    const generateNodesNamesField = (server) => {
      if (this.isNotEmptyArray(server.nodes)) {
        server.nodesNames = this.mapNodesIdsToNames(server.nodes, dataObject.nodesList);
      }
    };

    const setInternalProtocolDefaultValue = (server) => {
      server.nodeProtocol = 'https';
    };

    if (this.isNotEmptyArray(dataObject.routingServers)) {
      dataObject.routingServers.forEach(generateNodesNamesField);
      dataObject.routingServers.forEach(setInternalProtocolDefaultValue);
    }
    this.items = dataObject.routingServers;
  }

  private isNotEmptyArray(value): any {
    return Array.isArray(value) && value.length > 0;
  }

  private mapNodesIdsToNames(arrayOfIds, nodesList): any {
    let resultStr = '';

    if (this.isNotEmptyArray(arrayOfIds)) {
      arrayOfIds.forEach((id, index) => {
        if (index > 0) {
          resultStr += ', ';
        }
        resultStr += this.getNodeNameById(id, nodesList);
      });
    }
    return resultStr;
  }

  private getNodeNameById(id, nodesList): any {
    if (this.isNotEmptyArray(nodesList)) {
      const node = nodesList.find((n) => {
        return id === n.id;
      });

      return node ? node.name : '';
    }
    return '';
  }

  private buildRoutingServerObject(isEdit, server): any {
    const serverObject: any = {type: 'ROUTING', port: 443};

    if (isEdit) {
      serverObject.id = server.id;
      serverObject.name = server.name;
      serverObject.address = server.address;
      serverObject.protocol = server.protocol;
      serverObject.adminState = server.adminState;
      serverObject.operatorId = server.operatorId;
      serverObject.credentialsId = server.credentialsId;
      serverObject.confRouterPort = server.confRouterPort;
      serverObject.upgradeSequence = server.upgradeSequence;
      serverObject.tunEnabled = server.tunEnabled;
      serverObject.tunIpAddress = server.tunIpAddress;


    } else {
      serverObject.protocol = 'https';
      serverObject.operatorId = -1;
      serverObject.credentialsId = -1;
      serverObject.upgradeSequence = null;

    }

    return serverObject;
  }



  certificate(): void {
    this.dialogService.open(ServerCertificateDialogComponent, {
      dialogData: [this.selection.id],
      title: 'Certificate',
      cancelButtonText: 'Close',
      width: 1000,
    });
  }

  lockServer(): void {
    const server = _.cloneDeep(this.selection);
    const routingServerObject = this.buildRoutingServerObject(true, server);
    const dataObject = {routingServers: null};

    this.routingServersSettingsApiService.getRoutingServers(this.generateFilterParameters()).subscribe(value => {
      dataObject.routingServers = value;
      this.openLockConfirmationDialog(server, dataObject.routingServers, routingServerObject);
    });
  }

  openLockConfirmationDialog(server, routingServers, routingServerObject): any {
    if (server.adminState === 'UNLOCKED') {
      const numberOfServersPerNodeIdMap = {};

      server.nodes.forEach((serverNodeId) => {
        numberOfServersPerNodeIdMap[serverNodeId] = 0;
      });
      let confMessage;
      const numberOfUnlockedAvailableRouters = this.getUnlockedAndAvailableRouters(numberOfServersPerNodeIdMap, server, routingServers);
      const idsOfNodeWithoutRouter = this.findNodeWithoutRouter(numberOfServersPerNodeIdMap);
      if (idsOfNodeWithoutRouter.length > 0) {
        const names = _.filter(this.parameters.nodesList, node => idsOfNodeWithoutRouter.includes(node.id)).map(node => node.name);
        confMessage = this.getConfirmationMessage(server, numberOfUnlockedAvailableRouters, idsOfNodeWithoutRouter, names);
        this.openDialog(routingServerObject, server, confMessage);
      } else {
        confMessage = this.getConfirmationMessage(server, numberOfUnlockedAvailableRouters, idsOfNodeWithoutRouter, '');
        this.openDialog(routingServerObject, server, confMessage);
      }
    } else {
        routingServerObject.adminState = (server.adminState === 'UNLOCKED') ? 'LOCKED' : 'UNLOCKED';
        this.routingServersSettingsApiService.lockRoutingServer(routingServerObject).subscribe(() => {
          this.refresh();
        });
    }
  }

  openDialog(routingServerObject, server, confMessage ): void {
    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      data: {
        message: confMessage
      }
    });


    dialogRef.componentInstance.onApply.subscribe(() => {
      routingServerObject.adminState =  'LOCKED' ;
      this.routingServersSettingsApiService.lockRoutingServer(routingServerObject).subscribe(() => {
        dialogRef.close();
        this.refresh();
        this.snackBarService.display(`${this.logicalConjunctionService
          .generateAndDescription(this.selection, 'name')} successfully locked`);
      });
    });
  }
  getUnlockedAndAvailableRouters(numberOfServersPerNodeIdMap, server, routingServers): any {
    return routingServers.filter(s => {
      if (s.id !== server.id) {
        s.nodes.forEach((nodeId) => {
          numberOfServersPerNodeIdMap[nodeId]++;
        });
      }

      return (s.status === 'AVAILABLE') && (s.adminState === 'UNLOCKED') && s.id !== server.id;
    }).length;
  }

  findNodeWithoutRouter(numberOfServersPerNodeIdMap): any{
    const nodesWithoutAvailableRouter = [];
    for (const key in numberOfServersPerNodeIdMap) {
      if (numberOfServersPerNodeIdMap.hasOwnProperty(key)) {
        if (numberOfServersPerNodeIdMap[key] === 0) {
          nodesWithoutAvailableRouter.push(parseInt(key, 10));
        }
      }
    }
    return nodesWithoutAvailableRouter;
  }

  getConfirmationMessage(server, numberOfUnlockedAvailableRouters, idsOfNodeWithoutRouter, nameOfNodeWithoutRouter): any {
    let confirmationMessage;
    if ((numberOfUnlockedAvailableRouters === 0) && (server.status !== 'UNAVAILABLE')) {
      confirmationMessage = 'You are about to lock the only available routing server,' +
        ' this will disable all routing. Are you sure you want to proceed?';
    } else if (idsOfNodeWithoutRouter.length === 1) {
      confirmationMessage = 'You are about to lock the only routing server for node '
        + nameOfNodeWithoutRouter + ', it will become unroutable.\n Are you sure you want to proceed?';
    } else if (idsOfNodeWithoutRouter.length > 1) {
      confirmationMessage = 'You are about to lock the only routing server for multiple nodes ('
        + nameOfNodeWithoutRouter.slice(0, 5).join(' ,') + '...) as a result they will become unroutable.\n Are you sure you want to proceed?';
    } else {
      confirmationMessage = 'Are you sure you want to lock this routing server?';
    }
    return confirmationMessage;
  }


  lock(): void {
    this.lockServer();
  }

  unlock(): void {
    this.lockServer();
  }

  onFilter(filter: any): any {
    this.armTable.firstPage();
    this.filter = filter;
  }

  private sameUpgradeSequence(routingServerObject): boolean {
    if (_.isEmpty(this.items)) { return false; }

    return this.items.every(item => item.upgradeSequence === this.items[0].upgradeSequence) &&
      routingServerObject.upgradeSequence === this.items[0].upgradeSequence;
  }
}

